"use strict";
(function () {
  if (!customElements.get("vat-calculator")) {
    class VatCalculator extends HTMLElement {
      constructor() {
        super();
        this.elements = {};
        this.vatRate = 0;
        this.currencySymbol = this.dataset.currencySymbol || "$";
        this.country = this.dataset.vatCountry || "";
        this.taxesIncluded = this.dataset.taxes === "true";
        this.onSwatchChange = this.onSwatchChange.bind(this);
        this.onPriceDomChange = this.onPriceDomChange.bind(this);
        this.scheduleRefresh = this.scheduleRefresh.bind(this);
        this.defaultPrices = []; // store original base prices and tax flag
      }

      connectedCallback() {
        this.setupElements();
        this.calculateVatRate();
        if (this.vatRate === 0) {
          this.style.display = "none";
          return;
        }
        this.saveDefaultPrices();
        this.addEventListeners();
        this.startWatchingPriceChanges();
        if (this.elements.swatch) {
          this.elements.swatch.checked = this.taxesIncluded;
          this.updatePrices(this.taxesIncluded);
        }
      }

      disconnectedCallback() {
        if (this.elements.swatch) {
          this.elements.swatch.removeEventListener("change", this.onSwatchChange);
        }
        this.priceObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        document.removeEventListener("product-info:loaded", this.scheduleRefresh);
        if (this.refreshTimer) clearTimeout(this.refreshTimer);
      }

      setupElements() {
        this.elements.swatch = this.querySelector('[data-component="swatch"]');
        const prices = this.dataset.prices;
        if (prices) {
          const selector = prices.split(";").map((p) => `[data-component="${p}"]`).join(",");
          this.elements.pricesToUpdate = this.closest(".product__info-wrapper")?.querySelectorAll(selector);
        }
      }

      saveDefaultPrices() {
        if (!this.elements.pricesToUpdate) return;
        this.defaultPrices = [];
        this.elements.pricesToUpdate.forEach((el) => {
          const base = el.dataset.basePrice ? Number(el.dataset.basePrice) : this.parsePrice(el.innerText);
          this.defaultPrices.push({
            el: el,
            basePrice: base,
            includesTax: this.taxesIncluded
          });
        });
      }

      addEventListeners() {
        if (this.elements.swatch) this.elements.swatch.addEventListener("change", this.onSwatchChange);
      }

      startWatchingPriceChanges() {
        const root = this.closest(".product__info-wrapper") || this.parentElement;
        if (!root || typeof MutationObserver === "undefined") return;

        this.priceObserver?.disconnect();
        this.priceObserver = new MutationObserver(this.onPriceDomChange);
        this.priceObserver.observe(root, {
          childList: true,
          subtree: true
        });

        document.addEventListener("product-info:loaded", this.scheduleRefresh);

        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.variantChange) {
          this.variantChangeUnsubscriber?.();
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            this.scheduleRefresh();
          });
        }
      }

      onPriceDomChange(mutations) {
        const shouldRefresh = mutations.some((mutation) => {
          if (mutation.type !== "childList") return false;
          return [...mutation.addedNodes].some((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return false;
            return node.matches?.(".product__price-wrapper, [data-component='price-regular'], [data-component='price-sale'], [data-component='price-sale-compare']") || node.querySelector?.(".product__price-wrapper, [data-component='price-regular'], [data-component='price-sale'], [data-component='price-sale-compare']");
          });
        });

        if (shouldRefresh) this.scheduleRefresh();
      }

      scheduleRefresh() {
        if (this.refreshTimer) clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => {
          this.setupElements();
          this.saveDefaultPrices();
          if (this.elements.swatch) {
            this.updatePrices(this.elements.swatch.checked);
          }
        }, 0);
      }

      calculateVatRate() {
        const vatByCountry = {
          AT: 20, BE: 21, EE: 24, BG: 20, CY: 19, CZ: 21, DK: 25, FI: 25.5, FR: 20, DE: 19, GR: 24,
          HU: 27, HR: 25, IE: 23, IT: 22, LV: 21, LI: 8.1, LU: 17, MC: 20, MT: 18, NL: 21, PL: 23, PT: 23,
          ES: 21, SE: 25, RO: 19, SI: 22, TR: 20, AU: 10, GB: 20, NO: 25, CH: 8, US: 0, CA: 5
        };
        this.vatRate = vatByCountry[this.country] || 0;
      }

      parsePrice(text) {
        if (!text) return 0;
        let clean = text.replace(/[^0-9.,]/g, "");
        if (!clean) return 0;
        const lastComma = clean.lastIndexOf(",");
        const lastDot = clean.lastIndexOf(".");
        if (lastComma > lastDot) {
          clean = clean.replace(/\./g, "").replace(",", ".");
        } else {
          clean = clean.replace(/,/g, "");
        }
        return Number(clean) || 0;
      }

      formatPrice(value) {
        const locale = document.documentElement.lang || navigator.language || "en";
        const formatted = Number(value).toLocaleString(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true
        });
        return this.currencySymbol + formatted;
      }

      onSwatchChange(e) {
        const checked = e.target.checked;
        this.updatePrices(checked);
      }

      updatePrices(taxIncluded) {
        if (!this.defaultPrices || this.vatRate === 0) return;
        this.defaultPrices.forEach(({ el, basePrice, includesTax }) => {
          let updated;
          if (includesTax) {
            updated = taxIncluded ? basePrice : basePrice / (1 + this.vatRate / 100);
          } else {
            updated = taxIncluded ? basePrice * (1 + this.vatRate / 100) : basePrice;
          }
          el.innerText = this.formatPrice(updated);
        });
      }
    }

    customElements.define("vat-calculator", VatCalculator);
  }
})();
